export type HeyReachAccountsErrorKind = 'connect' | 'reconnect' | 'retry';

type UnknownRecord = Record<string, unknown>;

const INVALID_CONNECTION_CODES = new Set([
  'heyreach_invalid_key',
  'invalid_key',
  'needs_reconnect',
  'unauthorized',
  'forbidden',
]);

const MISSING_CONNECTION_CODES = new Set([
  'heyreach_not_connected',
  'heyreach_not_configured',
  'missing_key',
  'not_connected',
  'not_configured',
  'connection_missing',
  'no_api_key',
]);

function asRecord(value: unknown): UnknownRecord | undefined {
  if (!value || typeof value !== 'object') return undefined;
  return value as UnknownRecord;
}

function normalizeCode(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  return value.trim().toLowerCase();
}

function getBodyCodes(body: unknown): string[] {
  const record = asRecord(body);
  if (!record) return [];

  const nestedError = asRecord(record.error);
  return [
    normalizeCode(record.code),
    normalizeCode(record.reason),
    normalizeCode(record.connectionStatus),
    normalizeCode(record.error),
    normalizeCode(nestedError?.code),
    normalizeCode(nestedError?.reason),
    normalizeCode(nestedError?.connectionStatus),
  ].filter((value): value is string => Boolean(value));
}

/**
 * Classify an accounts-query failure without depending on one Axios interceptor
 * shape. The app currently exposes both `error.data` and the canonical
 * `error.response.data`, while tests and network failures may only have one.
 */
export function classifyHeyReachAccountsError(error: unknown): HeyReachAccountsErrorKind {
  const record = asRecord(error);
  const response = asRecord(record?.response);
  const statusCandidate = response?.status ?? record?.status;
  const status = typeof statusCandidate === 'number' ? statusCandidate : Number(statusCandidate);
  const codes = [
    ...getBodyCodes(response?.data),
    ...getBodyCodes(record?.data),
    normalizeCode(record?.code),
    normalizeCode(record?.reason),
    normalizeCode(record?.connectionStatus),
  ].filter((value): value is string => Boolean(value));

  if (status === 401 || status === 403 || codes.some((code) => INVALID_CONNECTION_CODES.has(code))) {
    return 'reconnect';
  }

  if (status === 404 || codes.some((code) => MISSING_CONNECTION_CODES.has(code))) {
    return 'connect';
  }

  return 'retry';
}

export function shouldRetryHeyReachAccountsRequest(failureCount: number, error: unknown): boolean {
  if (classifyHeyReachAccountsError(error) !== 'retry') return false;
  return failureCount < 2;
}
